import { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { Plus, Trash2, X, Mail, ShieldCheck, Power } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";

const inputCls = "w-full bg-[#050505] border border-white/10 rounded-xl px-4 py-3 text-white placeholder:text-[#666] focus:border-white/40 focus:outline-none transition-colors duration-300";

const PERMISSION_OPTIONS = [
  { id: "orders", label: "Bestellungen" },
  { id: "customers", label: "Kunden" },
  { id: "inquiries", label: "Anfragen" },
  { id: "pricing", label: "Preise" },
  { id: "discounts", label: "Rabattcodes" },
  { id: "revenue", label: "Umsatzstatistik" },
  { id: "invoices", label: "Rechnungen" },
  { id: "blog", label: "Ratgeber" },
  { id: "content", label: "Inhalte" },
  { id: "nav", label: "Tabs" },
  { id: "company", label: "Firmendaten" },
  { id: "settings", label: "Einstellungen" },
  { id: "protection", label: "Schutzpakete" },
];

function StaffModal({ staff, onClose, onSaved }) {
  const [email, setEmail] = useState(staff?.email || "");
  const [name, setName] = useState(staff?.name || "");
  const [password, setPassword] = useState("");
  const [permissions, setPermissions] = useState(staff?.permissions || []);
  const [saving, setSaving] = useState(false);

  const togglePerm = (id) => setPermissions((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));

  const save = async () => {
    if (!staff && (!email.trim() || !password.trim() || !name.trim())) {
      toast.error("Name, E-Mail und Passwort sind erforderlich.");
      return;
    }
    setSaving(true);
    try {
      let data;
      if (staff) {
        const payload = { name, permissions };
        if (password.trim()) payload.password = password;
        ({ data } = await api.patch(`/staff/${staff.id}`, payload));
      } else {
        ({ data } = await api.post("/staff", { email, password, name, permissions }));
      }
      onSaved(data, !staff);
      toast.success(staff ? "Mitarbeiter aktualisiert." : "Mitarbeiter angelegt.");
      onClose();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Speichern fehlgeschlagen.");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <motion.div initial={{ opacity: 0, scale: 0.96, y: 20 }} animate={{ opacity: 1, scale: 1, y: 0 }} data-testid="staff-modal"
        className="relative glass rounded-3xl p-5 sm:p-8 w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-6">
          <h3 className="font-display text-2xl">{staff ? "Mitarbeiter bearbeiten" : "Neuer Mitarbeiter"}</h3>
          <button onClick={onClose} className="text-[#888] hover:text-white"><X size={20} /></button>
        </div>
        <div className="space-y-4">
          <input data-testid="staff-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Name" className={inputCls} />
          <input data-testid="staff-email" type="email" disabled={!!staff} value={email} onChange={(e) => setEmail(e.target.value)} placeholder="E-Mail"
            className={`${inputCls} ${staff ? "opacity-50 cursor-not-allowed" : ""}`} />
          <input data-testid="staff-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)}
            placeholder={staff ? "Neues Passwort (optional)" : "Passwort"} className={inputCls} />
          <div>
            <label className="block text-xs font-mono uppercase tracking-widest text-[#888] mb-3">Berechtigungen (Tabs)</label>
            <div className="grid grid-cols-2 gap-2">
              {PERMISSION_OPTIONS.map((p) => (
                <label key={p.id} data-testid={`staff-perm-${p.id}`} className={`flex items-center gap-2 text-sm rounded-xl border px-3 py-2.5 cursor-pointer transition-colors ${permissions.includes(p.id) ? "border-white bg-white/10 text-white" : "border-white/10 text-[#999] hover:border-white/30"}`}>
                  <input type="checkbox" className="accent-white" checked={permissions.includes(p.id)} onChange={() => togglePerm(p.id)} />
                  {p.label}
                </label>
              ))}
            </div>
          </div>
        </div>
        <div className="flex gap-3 mt-6">
          <button onClick={save} disabled={saving} data-testid="staff-save" className="flex-1 rounded-full bg-white text-[#050505] py-3 font-medium hover:bg-[#d1d5db] transition-colors disabled:opacity-50">
            {saving ? "Speichern…" : "Speichern"}
          </button>
          <button onClick={onClose} className="px-6 rounded-full border border-white/15 text-[#ccc] hover:text-white transition-colors">Abbrechen</button>
        </div>
      </motion.div>
    </div>
  );
}

export default function StaffView() {
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/staff");
      setStaff(data);
    } catch (e) { toast.error("Mitarbeiter konnten nicht geladen werden."); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggleActive = async (s) => {
    try {
      const { data } = await api.patch(`/staff/${s.id}`, { active: !s.active });
      setStaff((p) => p.map((x) => (x.id === s.id ? data : x)));
      toast.success(data.active ? "Zugang aktiviert." : "Zugang deaktiviert.");
    } catch (e) { toast.error("Aktualisierung fehlgeschlagen."); }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/staff/${id}`);
      setStaff((p) => p.filter((x) => x.id !== id));
      toast.success("Mitarbeiter gelöscht.");
    } catch (e) { toast.error("Löschen fehlgeschlagen."); }
  };

  return (
    <div data-testid="staff-view">
      <div className="flex items-center justify-between mb-8 gap-4 flex-wrap">
        <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl">Mitarbeiter</h1>
        <button onClick={() => setModal({ staff: null })} data-testid="staff-new-btn"
          className="inline-flex items-center gap-2 rounded-full bg-white text-[#050505] px-5 py-2.5 text-sm font-medium hover:bg-[#d1d5db] transition-colors">
          <Plus size={16} /> Neuer Mitarbeiter
        </button>
      </div>
      <p className="text-sm text-[#888] mb-6 max-w-2xl">Mitarbeiter können sich mit ihrer eigenen E-Mail und ihrem Passwort anmelden und sehen nur die Tabs, für die du ihnen eine Berechtigung gibst.</p>

      {loading ? (
        <p className="text-[#888] font-mono text-sm py-20 text-center">Lädt…</p>
      ) : staff.length === 0 ? (
        <div data-testid="staff-empty-state" className="rounded-2xl border border-white/10 bg-[#0a0a0a] p-16 text-center">
          <p className="text-[#888]">Noch keine Mitarbeiter angelegt.</p>
        </div>
      ) : (
        <div className="space-y-3" data-testid="staff-list">
          {staff.map((s) => (
            <div key={s.id} data-testid={`staff-${s.id}`} className={`rounded-2xl border p-4 sm:p-6 grid grid-cols-1 lg:grid-cols-12 gap-4 items-start ${s.active ? "border-white/10 bg-[#0a0a0a]" : "border-red-500/20 bg-[#0a0a0a]/60 opacity-60"}`}>
              <div className="lg:col-span-5">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="text-lg">{s.name}</p>
                  <span data-testid={`staff-active-badge-${s.id}`} className={`text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 rounded-full ${s.active ? "bg-green-500/20 text-green-300 border border-green-500/30" : "bg-red-500/20 text-red-300 border border-red-500/30"}`}>
                    {s.active ? "Aktiv" : "Deaktiviert"}
                  </span>
                </div>
                <a href={`mailto:${s.email}`} className="inline-flex items-center gap-2 text-sm text-[#999] hover:text-white transition-colors mt-2"><Mail size={14} />{s.email}</a>
              </div>
              <div className="lg:col-span-5">
                <div className="flex items-center gap-1.5 flex-wrap">
                  <ShieldCheck size={13} className="text-[#666]" />
                  {s.permissions.length === 0 ? (
                    <span className="text-xs text-[#666]">Keine Berechtigungen</span>
                  ) : s.permissions.map((p) => (
                    <span key={p} className="text-xs px-2 py-0.5 rounded-full bg-white/10 text-[#ccc]">{PERMISSION_OPTIONS.find((o) => o.id === p)?.label || p}</span>
                  ))}
                </div>
              </div>
              <div className="lg:col-span-2 flex lg:justify-end gap-2">
                <button onClick={() => toggleActive(s)} data-testid={`staff-toggle-active-${s.id}`} title={s.active ? "Deaktivieren" : "Aktivieren"}
                  className={`w-9 h-9 rounded-lg border flex items-center justify-center transition-colors ${s.active ? "border-white/10 text-[#888] hover:text-red-400 hover:border-red-400/40" : "border-white/10 text-[#888] hover:text-green-400 hover:border-green-400/40"}`}>
                  <Power size={16} />
                </button>
                <button onClick={() => setModal({ staff: s })} data-testid={`staff-edit-${s.id}`} className="px-3 h-9 rounded-lg border border-white/10 text-xs text-[#888] hover:text-white hover:border-white/40 transition-colors">Bearbeiten</button>
                <button onClick={() => remove(s.id)} data-testid={`staff-delete-${s.id}`} className="w-9 h-9 rounded-lg border border-white/10 flex items-center justify-center text-[#888] hover:text-red-400 hover:border-red-400/40 transition-colors"><Trash2 size={16} /></button>
              </div>
            </div>
          ))}
        </div>
      )}

      <AnimatePresence>
        {modal && (
          <StaffModal
            staff={modal.staff}
            onClose={() => setModal(null)}
            onSaved={(d, isNew) => setStaff((p) => (isNew ? [d, ...p] : p.map((s) => (s.id === d.id ? d : s))))}
          />
        )}
      </AnimatePresence>
    </div>
  );
}
